/**
 * CLIENT DEMO MODE - przeglądarka sama odtwarza lokalne pliki z katalogu.
 *
 * Jedno źródło prawdy: pozycja wybrana przez kolejkę (`queue.current`) jest
 * jednocześnie plikiem ładowanym do <audio> i obiektem publikowanym w store
 * jako `currentItem`. Nie ma osobnych, niezależnie wybieranych metadanych.
 */

import { toBroadcastItem, toUpcomingItem } from './catalog';
import { useRadioStore } from './radio.store';
import type { RadioEngine, RadioStatus } from './radio.types';
import type { RadioQueue } from './radioQueue';

/** Ile pozycji z rzędu może się nie wczytać, zanim uznamy, że transmisja nie jest gotowa. */
const MAX_CONSECUTIVE_FAILURES = 5;

export interface DemoEngineOptions {
  audio: HTMLAudioElement;
  queue: RadioQueue;
  isOnline?: () => boolean;
}

export function createDemoEngine({
  audio,
  queue,
  isOnline = () => navigator.onLine !== false,
}: DemoEngineOptions): RadioEngine {
  let wantsPlayback = false;
  let failures = 0;
  let emission = 0;
  /** Czy bieżąca pozycja kolejki jest już załadowana do <audio>. */
  let loaded = false;

  const store = (): ReturnType<typeof useRadioStore.getState> => useRadioStore.getState();
  const setStatus = (status: RadioStatus): void => store().setStatus(status);

  /** Publikuje stan kolejki - od tej chwili UI i Media Session pokazują tę pozycję. */
  function publish(): void {
    emission += 1;
    loaded = false;
    const { current, next } = queue;
    store().setCurrentItem(current ? toBroadcastItem(current, emission) : null);
    store().setNextItem(next ? toUpcomingItem(next) : null);
  }

  function stop(status: RadioStatus): void {
    wantsPlayback = false;
    setStatus(status);
  }

  function attemptPlay(): void {
    audio.play().catch((error: unknown) => {
      // Błędy ładowania obsługuje zdarzenie `error`; tu tylko blokada autoodtwarzania.
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        stop('paused');
      }
    });
  }

  function load(): void {
    const item = queue.current;
    if (!item) {
      stop('offline');
      return;
    }
    audio.src = item.src;
    loaded = true;
    if (wantsPlayback) attemptPlay();
  }

  function advance(): void {
    queue.advance();
    publish();
    if (wantsPlayback) load();
  }

  const onPlaying = (): void => {
    if (!wantsPlayback) return;
    failures = 0;
    store().markCurrentStarted();
    setStatus('playing');
  };

  const onWaiting = (): void => {
    if (wantsPlayback) setStatus('loading');
  };

  const onPause = (): void => {
    // Pauza z zewnątrz (system, słuchawki, telefon), a nie koniec utworu.
    if (wantsPlayback && store().status === 'playing' && !audio.ended) stop('paused');
  };

  const onTimeUpdate = (): void => {
    const position = Math.floor(audio.currentTime);
    if (position !== store().position) store().setPosition(position);
  };

  const onDurationChange = (): void => {
    // Długość bierzemy z tego samego pliku, który gra.
    if (loaded && Number.isFinite(audio.duration) && audio.duration > 0) {
      store().patchCurrentItem({ duration: audio.duration });
    }
  };

  const onEnded = (): void => {
    if (wantsPlayback) advance();
  };

  const onError = (): void => {
    if (!wantsPlayback) return;
    failures += 1;

    if (!isOnline()) {
      stop('offline');
      return;
    }
    if (failures >= MAX_CONSECUTIVE_FAILURES) {
      // Plików nie ma na serwerze - radio po prostu nie jest jeszcze gotowe.
      stop('offline');
      return;
    }
    // Pozycja, która się nie wczytała, nie trafia do historii (nie zaczęła grać).
    setStatus('loading');
    advance();
  };

  const onOnline = (): void => {
    if (store().status === 'offline' && queue.current) setStatus('paused');
  };

  const onOffline = (): void => {
    if (!wantsPlayback) setStatus('offline');
  };

  audio.addEventListener('playing', onPlaying);
  audio.addEventListener('waiting', onWaiting);
  audio.addEventListener('pause', onPause);
  audio.addEventListener('timeupdate', onTimeUpdate);
  audio.addEventListener('durationchange', onDurationChange);
  audio.addEventListener('ended', onEnded);
  audio.addEventListener('error', onError);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  publish();
  if (!queue.current) setStatus('offline');

  return {
    play() {
      if (!queue.current || !isOnline()) {
        stop('offline');
        return;
      }
      wantsPlayback = true;
      failures = 0;
      setStatus('loading');

      if (loaded && !audio.error && !audio.ended) {
        attemptPlay();
      } else {
        load();
      }
    },
    pause() {
      stop(queue.current ? 'paused' : 'offline');
      audio.pause();
    },
    next() {
      if (!queue.current) return;
      failures = 0;
      if (wantsPlayback) setStatus('loading');
      advance();
    },
    dispose() {
      wantsPlayback = false;
      audio.pause();
      audio.removeAttribute('src');
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    },
  };
}
